import FIELDS from '../PoliceReportFields';
import moment from 'moment';
import validateDocumentNumber from './validateDocumentNumber';
import PoliceReportConstants from '../police.constants';
import { isEmpty, validateEmail } from 'utils/TextUtils';
import isValidPostalCode from './validatePostalCode';
import mandatoryFields from '../PoliceReportMandatoryFields';
import { getMunicipalityByPostalCode } from '../PoliceReportParse.utils';

const { ADULT_AGE, FAMILY_RELATIONSHIP } = PoliceReportConstants;

function isAdult(date) {
  const birthDate = moment(date, ['DD-MM-YYYY', 'YYYY-MM-DD']);
  const today = moment();
  return birthDate.isValid() && today.diff(birthDate, 'years') > ADULT_AGE;
}

function isMandatoryFieldEmpty(report, field) {
  const isEmptyField = field => isEmpty(report?.[field]);
  const birthDate = moment(report?.[FIELDS.BIRTHDATE_KEY], ['DD-MM-YYYY', 'YYYY-MM-DD']);
  const today = moment();
  const guestIsAdult = birthDate.isValid() && today.diff(birthDate, 'years') > ADULT_AGE;

  function isMandatoryField() {
    const requiredFields = [
      ...mandatoryFields,
      report?.[FIELDS.COUNTRY_KEY] === 'ESP'
        ? FIELDS.MUNICIPALITY_KEY
        : FIELDS.MUNICIPALITYNAME_KEY,
      guestIsAdult && report?.[FIELDS.CHILDREN_KEY] > 0 ? FIELDS.FAMILYRELATIONSHIP_KEY : null,
      guestIsAdult ? FIELDS.CONTACTVALUE_KEY : null,
    ].filter(item => item != null);

    return requiredFields.includes(field) && isEmptyField(field);
  }

  function isSurnameField() {
    const mandatorySurnameDocForSecondSurname = ['dni'];
    return (
      field === FIELDS.SECONDSURNAME_KEY &&
      mandatorySurnameDocForSecondSurname.includes(report?.[FIELDS.DOCUMENTTYPE_KEY]) &&
      isEmptyField(field)
    );
  }

  function isDocumentSupportField() {
    const mandatoryDocForDocSupport = ['dni', 'residencePermit'];
    return (
      field === FIELDS.DOCUMENTSUPPORT_KEY &&
      mandatoryDocForDocSupport.includes(report?.[FIELDS.DOCUMENTTYPE_KEY]) &&
      guestIsAdult &&
      isEmptyField(field)
    );
  }

  function isDocumentTypeOrNumberField() {
    return (
      (field === FIELDS.DOCUMENTTYPE_KEY || field === FIELDS.DOCUMENTNUMBER_KEY) &&
      guestIsAdult &&
      isEmptyField(field)
    );
  }

  function isFamilyRelationshipField() {
    return field === FIELDS.FAMILYRELATIONSHIP_KEY && !guestIsAdult && isEmptyField(field);
  }

  return (
    isMandatoryField() ||
    isSurnameField() ||
    isDocumentSupportField() ||
    isDocumentTypeOrNumberField() ||
    isFamilyRelationshipField()
  );
}

function getFieldValidations(report, translate) {
  const fieldValidations = {
    [FIELDS.DOCUMENTNUMBER_KEY]: {
      validate: guest => {
        const docType = guest[FIELDS.DOCUMENTTYPE_KEY];
        const docNumber = guest[FIELDS.DOCUMENTNUMBER_KEY];
        const country = guest[FIELDS.COUNTRY_KEY];

        if (docNumber?.length > 15) {
          return false;
        }

        if (!isAdult(guest[FIELDS.BIRTHDATE_KEY])) {
          if (guest[FIELDS.DOCUMENTSUPPORT_KEY] !== '' || docNumber !== '') {
            return validateDocumentNumber(docType, docNumber, country);
          } else {
            return true;
          }
        }

        return !!validateDocumentNumber(docType, docNumber, country);
      },
    },

    [FIELDS.ZIPCODE_KEY]: {
      validate: guest => {
        const zip = guest?.[FIELDS.ZIPCODE_KEY];
        const country = guest?.[FIELDS.COUNTRY_KEY];
        const municipality = guest?.[FIELDS.MUNICIPALITY_KEY];

        if (country?.toUpperCase() !== 'ESP') return true;

        return isValidPostalCode(zip, country);
      },
      //&& getMunicipalityByPostalCode(zip, country, municipality) mirar porque da muchos errores.
    },

    [FIELDS.CONTACTVALUE_KEY]: {
      validate: guest => {
        const contactType = guest?.[FIELDS.CONTACTTYPE_KEY] || '';
        const contactValue = guest?.[FIELDS.CONTACTVALUE_KEY] || '';
        const guestIsAdult = isAdult(guest?.[FIELDS.BIRTHDATE_KEY]);

        if (!contactValue.length && !guestIsAdult) {
          return true;
        }

        if (contactType === 'email') {
          const emailRegex = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
          return contactValue.length <= 60 && emailRegex.test(contactValue);
        } else if (contactType === 'telefono') {
          const phoneNormalized = contactValue.replace(/\s+/g, '');
          const phoneRegex = /^\+?\d{5,20}$/;
          return phoneRegex.test(phoneNormalized);
        }

        return true;
      },
    },

    [FIELDS.ADDRESS_KEY]: {
      validate: guest => {
        const addressKey = guest?.[FIELDS.ADDRESS_KEY] || '';
        const invalidXmlChars = /[<>&"']/;

        return addressKey.length <= 100 && !invalidXmlChars.test(addressKey);
      },
    },

    [FIELDS.BIRTHDATE_KEY]: {
      validate: guest => {
        const date = moment(guest?.[FIELDS.BIRTHDATE_KEY], ['DD-MM-YYYY', 'YYYY-MM-DD']);
        return date.isValid() && date <= moment() && date.year() >= 1900;
      },
    },
    [FIELDS.DOCUMENTSUPPORT_KEY]: {
      validate: guest => {
        const docSupport = guest?.[FIELDS.DOCUMENTSUPPORT_KEY] || '';
        const docNumber = guest?.[FIELDS.DOCUMENTNUMBER_KEY] || '';
        const docType = guest?.[FIELDS.DOCUMENTTYPE_KEY];
        const country = guest?.[FIELDS.COUNTRY_KEY];
        const birthDate = guest?.[FIELDS.BIRTHDATE_KEY];

        const guestIsAdult = isAdult(birthDate);
        const mandatoryDocForDocSupport = ['dni', 'residencePermit'];
        const isResidencePermit = docType === 'residencePermit';

        if (!guestIsAdult) {
          if (
            (docSupport !== '' || docNumber !== '') &&
            mandatoryDocForDocSupport.includes(docType)
          ) {
            return !!validateDocumentNumber(
              FIELDS.DOCUMENTSUPPORT_KEY,
              docSupport,
              country,
              isResidencePermit
            );
          } else {
            return true;
          }
        }

        if (mandatoryDocForDocSupport.includes(docType)) {
          return !!validateDocumentNumber(
            FIELDS.DOCUMENTSUPPORT_KEY,
            docSupport,
            country,
            isResidencePermit
          );
        }

        return true;
      },
    },
    [FIELDS.FAMILYRELATIONSHIP_KEY]: {
      validate: guest => {
        const birthDate = guest?.[FIELDS.BIRTHDATE_KEY];
        const relation = guest?.[FIELDS.FAMILYRELATIONSHIP_KEY];
        const guestIsAdult = isAdult(birthDate);

        if (guestIsAdult) {
          return FAMILY_RELATIONSHIP.map(f => f.value).includes(relation);
        }

        return true;
      },
    },
  };

  if (translate) {
    fieldValidations[FIELDS.FAMILYRELATIONSHIP_KEY].error = translate(
      'policeReport.error.familyRelationship'
    );
    fieldValidations[FIELDS.DOCUMENTNUMBER_KEY].error = translate(
      'policeReport.error.documentNumber'
    );
    fieldValidations[FIELDS.ZIPCODE_KEY].error = translate('policeReport.error.zipCode');
    fieldValidations[FIELDS.CONTACTVALUE_KEY].error = translate('policeReport.error.contactError');
    fieldValidations[FIELDS.BIRTHDATE_KEY].error = translate('policeReport.error.birthDate');
    fieldValidations[FIELDS.DOCUMENTSUPPORT_KEY].error = translate(
      'policeReport.error.documentSupport'
    );
    fieldValidations[FIELDS.ADDRESS_KEY].error = translate('policeReport.error.address');
  }

  return fieldValidations;
}

export default { isMandatoryFieldEmpty, getFieldValidations };
